import { BrowserModule } from '@angular/platform-browser';
import { InjectionToken, NgModule } from '@angular/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoggerModule } from 'ngx-logger';

// Ngx-bootstrap
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { deLocale } from 'ngx-bootstrap/locale';
defineLocale('de', deLocale);

// Dayjs
import * as dayjs from 'dayjs';
import * as localizedFormat from 'dayjs/plugin/localizedFormat';
import * as localeData from 'dayjs/plugin/localeData';
import * as customParseFormat from 'dayjs/plugin/customParseFormat'; // Needed to parse time (without date --> HH:mm)
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { environment } from 'src/environments/environment';
import {LoginComponent} from '@app/login/login/login.component';
import { TokenLoginComponent } from './login/token-login/token-login.component';
import { from, Observable, of } from 'rxjs';
import { Loader } from '@googlemaps/js-api-loader';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { GoogleMapsModule } from '@angular/google-maps';
import { UrgentNoticeComponent } from './urgent-notice/urgent-notice/urgent-notice.component';
import { NotificationOverviewComponent } from './notification-overview/notification-overview/notification-overview.component';
import { WhatsnewComponent } from './whatsnew/whatsnew/whatsnew.component';


export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/locales/locale-', '.json?v=' + environment.version);
}

/**
 * Used to make sure, Google Maps API is only loaded once (and only if it is needed).
 *
 * Use it in your component (inject it in constructor and use it to conditionally show the map):
 *
 * Typescript:
 * ```typescript
 * constructor(@Inject(MAPS_API_LOADED) public mapsApiLoaded: Observable<boolean>) {
 * ```
 *
 * HTML:
 * ```html
 * <google-map ngIf="mapsApiLoaded | async"></google-map>
 * ```
 *
 * Based on https://github.com/angular/components/issues/17828#issuecomment-772784722
 */
export const MAPS_API_LOADED = new InjectionToken<Observable<boolean>>('Google Maps API loaded');

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    TokenLoginComponent
  ],
  imports: [
    BrowserModule,
    GoogleMapsModule,
    BrowserAnimationsModule, // Required for ngx-bootstrap (and in genaral for animations)
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    LoggerModule.forRoot({
      level: environment.logLevel,
      // Shows the name of the file which caused the log entry only in devPage mode
      enableSourceMaps: !environment.production
    }),
    // ngx-bootstrap
    BsDropdownModule.forRoot(),
    TooltipModule.forRoot(),
    PaginationModule.forRoot(),
    BsDatepickerModule.forRoot(),
    ProgressbarModule.forRoot(),
    // Modules from this project
    AppRoutingModule,
    CoreModule,
    SharedModule
  ],
  providers: [
    { provide: MAPS_API_LOADED, useFactory: loadMapsApi }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

/**
 * Load Google Maps API
 */
function loadMapsApi() {
  const loader = new Loader({
    apiKey: 'AIzaSyCug6M79LjjmodXv6xYqBk1yGqxgqFJ1tM',
    version: 'quarterly'
  });

  return from(loader.load()).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    map(() => true),
    catchError(e => {
      return of(false);
    })
  );
}
